import React, { useState, Fragment, useCallback } from 'react';
import loadable from '@loadable/component';
import styled from 'styled-components';
import { Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import Container from 'ls-common-client/dist/components/Container';
import AlphaList from '../../components/AlphaList';
import AnimatedBanner from '../../components/AnimatedBanner';
import CenteredLoader from '../../components/CenteredLoader';
import Breadcrumbs from '../../components/Breadcrumbs';
import AddABusiness from '../../components/AddABusiness';
import Header from '../../components/Header';
import LazyLoad from '../../components/LazyLoad';
import ScrollToTop from '../../components/ScrollToTop';
import ReviewForm from '../../components/ReviewForm';
import LayoutContainer from '../../components/LayoutContainer';
import ArcBackground from '../../components/Landing/Background/Arc';
import categoryUrl from '../../utils/categoryUrl';
import { searchSuggestSitemapCategoriesQuery } from '../../gql/search';
import Meta from './Meta';

const AppDownload = loadable(
  () => import('../../components/Landing/AppDownload'),
  {
    fallback: <CenteredLoader />,
  }
);
const B2bSection = loadable(() => import('../../components/B2bSection'), {
  fallback: <CenteredLoader />,
});
const DigitalMarketingBlogs = loadable(
  () => import('../../components/Blogs/DigitalMarketing'),
  {
    fallback: <CenteredLoader />,
  }
);
const DigitalMarketingBanner = loadable(
  () => import('../../components/DigitalMarketingBanner'),
  {
    fallback: <CenteredLoader />,
  }
);

const StyledRoot = styled(Container)`
  background: #ffffff;
  .Header__Landing__root {
    background-image: linear-gradient(180deg, #98b0fd -45%, #2d60fb);
  }
  h1 {
    color: #ffffff;
    font-size: 50px;
    margin-top: 60px;
    font-family: 'Circular';
    line-height: 1em;
    text-shadow: 0 2px 2px rgba(0, 0, 0, 0.2);
    margin-bottom: 10px;
  }
  .AlphaList__pagination {
    margin-top: -200px;
  }
  .DigitalMarketingBanner__root {
    margin-top: -65px;
  }

  .CategorySitemap__Breadcrumbs {
    margin-top: 40 !important;
  }
  .CategorySitemap__Seo {
    min-height: 2183px;
  }

  @media ${props => props.theme.mediaQueries.tablet} {
    .CategorySitemap__Seo {
      min-height: 2701px;
    }
  }

  @media ${props => props.theme.mediaQueries.mobile} {
    .CategorySitemap__Seo {
      min-height: 3024px;
    }
    h1 {
      font-size: 30px;
      margin-top: 50px;
    }
    .DigitalMarketingBanner__root {
      margin-top: 0;
    }
    .AlphaList__pagination {
      margin-top: -63px;
    }
  }
`;

const StyledBanner = styled(Container)`
  min-height: 450px;
  padding: 10px 0;
  @media ${props => props.theme.mediaQueries.mobile} {
    min-height: 320px;
  }
`;

const ssr = ({ params: { letter = 'a' } }) => {
  return [
    {
      name: 'SuggestSitemapCategories',
      query: searchSuggestSitemapCategoriesQuery,
      variables: {
        params: {
          keyword: letter,
        },
      },
    },
  ];
};

const CategorySitemap = ({ match }) => {
  const { params } = match || {};
  const { letter: paramLetter } = params || {};

  const { data: SuggestSitemapCategoriesData, refetch, loading } = useQuery(
    searchSuggestSitemapCategoriesQuery,
    {
      variables: {
        params: {
          keyword: paramLetter || 'a',
        },
      },
    }
  );

  const [refetchLoading, setRefetchLoading] = useState();
  const [alphaLetter, setAlphaLetter] = useState(paramLetter || 'a');

  const { searchSuggestSitemapCategories } = SuggestSitemapCategoriesData || {};
  const { data } = searchSuggestSitemapCategories || {};
  const { attributes } = (data && data[0]) || {};
  const { categories } = attributes || {};

  const fetchData = async ({ letter, isEmpty }) => {
    setRefetchLoading(true);
    setAlphaLetter(letter);
    if (isEmpty) {
      await refetch({
        params: {
          keyword: letter,
        },
      });
    }
    setRefetchLoading(false);
  };

  const setLinkItem = item => {
    return (
      <Link to={categoryUrl(item.name || item.web_name)}>
        {item.name || item.web_name}
      </Link>
    );
  };

  const setData = useCallback(
    value => {
      if (value && !!value.length) {
        if (value[0].web_name && alphaLetter) {
          const rgx = new RegExp(`^[${alphaLetter}](.*)$`, 'gim');
          if (rgx.test(value[0].web_name[0].toLowerCase())) return value;
        }
      }
      return [];
    },
    [alphaLetter]
  );

  return (
    <StyledRoot className="CategorySitemap__root">
      <ScrollToTop />
      <Meta
        titleName={`Business Categories ${alphaLetter.toUpperCase()}`}
        canonical={match.url}
        letter={alphaLetter}
      />
      <Header slideIn />
      <AnimatedBanner>
        <LayoutContainer>
          <StyledBanner className="CategorySitemap__banner">
            <h1 className="CategorySitemap__heading">
              Business Categories {alphaLetter.toUpperCase()}
            </h1>
          </StyledBanner>
        </LayoutContainer>
      </AnimatedBanner>
      <ArcBackground>
        <AlphaList
          prepend={['0-9']}
          letter={alphaLetter || 'a'}
          data={categories}
          displayItem={setLinkItem}
          setData={setData}
          fetchData={fetchData}
          loading={loading || refetchLoading}
          emptyStateText={() => (
            <Fragment>
              <span>
                Looks like there are no categories in{' '}
                {alphaLetter.toUpperCase()} yet.
              </span>
              <span>Choose another letter to see if you have better luck.</span>
            </Fragment>
          )}
        />
      </ArcBackground>
      <LazyLoad className="CategorySitemap__Seo">
        <DigitalMarketingBlogs
          style={{
            background: '#ffffff',
          }}
        />
        <B2bSection />
        <AppDownload />
        <DigitalMarketingBanner />
      </LazyLoad>
      <LayoutContainer className="CategorySitemap__Breadcrumbs">
        <Breadcrumbs
          data={[
            {
              breadcrumb: 'Localsearch',
              url: '/',
            },
            {
              url: 'sitemap',
              breadcrumb: 'Sitemap',
            },
          ]}
        />
      </LayoutContainer>
      <ReviewForm />
      <AddABusiness />
    </StyledRoot>
  );
};

export { ssr };

export default CategorySitemap;
